import { CampoAlteracao, ParteAlteracao, AnexoAlteracao } from "./types";
import { mapearAnexos } from "../../Compartilhado/utils";
import { mapearTituloBancoParaExibicao } from "../../../../../../../utils/servicoMapper";
import { formatFileSize } from "../../../../../../../utility/byteSizeUtils";
import { formatCurrency } from "../../../../../../../utility/currencyUtils";
import { parseJsonSafely } from "../../../../../../../utility/jsonUtils";
import { extractFilename } from "../../../../../../../utility/filePathUtils";

const formatDate = (value: string | Date | null | undefined, includeTimezone?: string): string => {
    if (!value) return '—';
    try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return '—';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const dateStr = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        return includeTimezone ? `${dateStr} ${includeTimezone}` : dateStr;
    } catch {
        return '—';
    }
};

const applyCNJMask = (value: string | undefined | null): string => {
    if (!value) return '—';
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 7) return numbers;
    if (numbers.length <= 9) return `${numbers.slice(0, 7)}-${numbers.slice(7)}`;
    if (numbers.length <= 13) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9)}`;
    if (numbers.length <= 14) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13)}`;
    if (numbers.length <= 16) return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13, 14)}.${numbers.slice(14)}`;
    return `${numbers.slice(0, 7)}-${numbers.slice(7, 9)}.${numbers.slice(9, 13)}.${numbers.slice(13, 14)}.${numbers.slice(14, 16)}.${numbers.slice(16, 20)}`;
};

const formatCEP = (value: string | undefined | null): string => {
    if (!value) return '—';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 8) {
        return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
    }
    return value;
};

const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined || value === '') return '—';
    
    const keyLower = key.toLowerCase();
    
    if (keyLower.includes('valor')) {
        return formatCurrency(value);
    }
    
    if (keyLower.includes('data') || keyLower.includes('prazo') || keyLower.includes('criado') || keyLower.includes('modificado')) {
        return formatDate(value);
    }
    
    if (keyLower.includes('processo')) {
        return applyCNJMask(value);
    }
    
    if (keyLower === 'cep') {
        return formatCEP(value);
    }
    
    if (typeof value === 'boolean') {
        return value ? 'Sim' : 'Não';
    }
    
    return String(value);
};

const getFieldLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
        'id_comarca': 'Comarca',
        'id_servico': 'Área do Direito',
        'titulo_servico': 'Área do Direito',
        'id_usuario': 'Profissional',
        'id_operador': 'Operador',
        'uf_comarca': 'Estado',
        'cidade_comarca': 'Comarca',
        'numero_processo': 'Número do Processo',
        'nome_cliente': 'Nome do Cliente',
        'codigo_cliente': 'Código do Cliente',
        'valor_cliente': 'Valor do Cliente',
        'valor_profissional': 'Valor do Serviço',
        'valor_adiantado': 'Valor Adiantado',
        'prazo_aceite': 'Prazo para aceite',
        'prazo_para_conclusao': 'Prazo para conclusão',
        'dias_uteis_para_conclusao': 'Dias Úteis para Conclusão',
        'data_conclusao': 'Data de Conclusão',
        'data_inicio': 'Data de Início',
        'data': 'Prazo Fatal',
        'data_tz': 'Fuso Horário',
        'orientacao': 'Orientações Prévias',
        'orientacoes': 'Orientações Prévias',
        'status': 'Status',
        'tipo_audiencia': 'Tipo',
        'profissional_necessario': 'Profissional Necessário',
        'modalidade': 'Modalidade',
        'endereco_virtual': 'Endereço Virtual',
        'rua': 'Rua',
        'bairro': 'Bairro',
        'cep': 'CEP',
        'numero': 'Número',
        'complemento': 'Complemento',
        'natureza': 'Natureza',
        'lucro': 'Lucro',
        'lucro_porcentagem': 'Lucro (%)',
        'criado': 'Criado em',
        'modificado': 'Modificado em',
    };
    
    return labelMap[key] || key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const isDadosVazio = (dados: any): boolean => {
    if (!dados) return true;
    if (typeof dados !== 'object') return false;
    const keys = Object.keys(dados);
    if (keys.length === 0) return true;
    if (keys.length === 1 && dados._removido === true) return true;
    return false;
};

export const mapearDadosPropostaEditada = (logs: any[]): CampoAlteracao[] => {
    const logProposta = logs.find((log: any) => log.entidade_nome === 'proposta');
    
    if (!logProposta) {
        return [];
    }
    
    const dadosAntes = parseJsonSafely(logProposta.dados_antes);
    const dadosDepois = parseJsonSafely(logProposta.dados_depois);
    
    if (!dadosAntes && !dadosDepois) {
        return [];
    }
    
    const mudancas: CampoAlteracao[] = [];
    const allKeys = Array.from(new Set([
        ...Object.keys(dadosAntes || {}),
        ...Object.keys(dadosDepois || {})
    ]));
    
    const camposIgnorados = [
        'modificado_em',
        'modificado',
        'id_proposta',
        'id_operador',
        'id_admin',
        'criado_em',
        'criado'
    ];
    
    const camposProcessados = new Set<string>();
    
    for (const key of allKeys) {
        if (camposIgnorados.includes(key.toLowerCase())) {
            continue;
        }
        
        if (key === 'titulo_servico' && camposProcessados.has('id_servico')) {
            continue;
        }
        
        if ((key === 'uf_comarca' || key === 'cidade_comarca') && camposProcessados.has('id_comarca')) {
            continue;
        }
        
        const valorAntes = dadosAntes?.[key];
        const valorDepois = dadosDepois?.[key];
        
        if (JSON.stringify(valorAntes) !== JSON.stringify(valorDepois)) {
            let antesFormatado = formatValue(key, valorAntes);
            let depoisFormatado = formatValue(key, valorDepois);
            let campoLabel = getFieldLabel(key);
            
            if (key === 'data_tz') {
                if (valorAntes) {
                    const tzMap: Record<string, string> = {
                        'BRT': 'BRT (UTC-3) - Brasília',
                        'AMT': 'AMT (UTC-4) - Manaus',
                        'ACT': 'ACT (UTC-5) - Rio Branco',
                        'FNT': 'FNT (UTC-2) - Fernando de Noronha',
                    };
                    antesFormatado = tzMap[valorAntes] || valorAntes;
                }
                if (valorDepois) {
                    const tzMap: Record<string, string> = {
                        'BRT': 'BRT (UTC-3) - Brasília',
                        'AMT': 'AMT (UTC-4) - Manaus',
                        'ACT': 'ACT (UTC-5) - Rio Branco',
                        'FNT': 'FNT (UTC-2) - Fernando de Noronha',
                    };
                    depoisFormatado = tzMap[valorDepois] || valorDepois;
                }
            } else if (key === 'data') {
                antesFormatado = formatDate(valorAntes, dadosAntes?.data_tz || 'BRT');
                depoisFormatado = formatDate(valorDepois, dadosDepois?.data_tz || 'BRT');
            } else if (key === 'prazo_aceite' || key === 'prazo_para_conclusao') {
                antesFormatado = formatDate(valorAntes, dadosAntes?.data_tz || 'BRT');
                depoisFormatado = formatDate(valorDepois, dadosDepois?.data_tz || 'BRT');
            } else if (key === 'id_servico') {
                const tituloAntes = mapearTituloBancoParaExibicao(dadosAntes?.titulo_servico);
                const tituloDepois = mapearTituloBancoParaExibicao(dadosDepois?.titulo_servico);
                antesFormatado = tituloAntes || formatValue(key, valorAntes);
                depoisFormatado = tituloDepois || formatValue(key, valorDepois);
                campoLabel = 'Área do Direito';
                camposProcessados.add('id_servico');
            } else if (key === 'id_comarca') {
                const ufAntes = dadosAntes?.uf_comarca;
                const cidadeAntes = dadosAntes?.cidade_comarca;
                const ufDepois = dadosDepois?.uf_comarca;
                const cidadeDepois = dadosDepois?.cidade_comarca;
                
                const ufMudou = ufAntes !== ufDepois;
                const cidadeMudou = cidadeAntes !== cidadeDepois;
                
                if (ufMudou && cidadeMudou) {
                    mudancas.push({
                        campo: 'Estado',
                        antes: formatValue('uf_comarca', ufAntes),
                        depois: formatValue('uf_comarca', ufDepois)
                    });
                    mudancas.push({
                        campo: 'Comarca',
                        antes: formatValue('cidade_comarca', cidadeAntes),
                        depois: formatValue('cidade_comarca', cidadeDepois)
                    });
                    camposProcessados.add('id_comarca');
                    camposProcessados.add('uf_comarca');
                    camposProcessados.add('cidade_comarca');
                    continue;
                } else if (cidadeMudou) {
                    antesFormatado = formatValue('cidade_comarca', cidadeAntes);
                    depoisFormatado = formatValue('cidade_comarca', cidadeDepois);
                    campoLabel = 'Comarca';
                } else if (ufMudou) {
                    antesFormatado = formatValue('uf_comarca', ufAntes);
                    depoisFormatado = formatValue('uf_comarca', ufDepois);
                    campoLabel = 'Estado';
                } else {
                    antesFormatado = formatValue('cidade_comarca', cidadeAntes);
                    depoisFormatado = formatValue('cidade_comarca', cidadeDepois);
                    campoLabel = 'Comarca';
                }
                camposProcessados.add('id_comarca');
                camposProcessados.add('uf_comarca');
                camposProcessados.add('cidade_comarca');
            } else if (key === 'uf_comarca') {
                if (camposProcessados.has('id_comarca')) {
                    continue;
                }
                antesFormatado = formatValue(key, valorAntes);
                depoisFormatado = formatValue(key, valorDepois);
                campoLabel = 'Estado';
                camposProcessados.add('uf_comarca');
            } else if (key === 'cidade_comarca') {
                if (camposProcessados.has('id_comarca')) {
                    continue;
                }
                antesFormatado = formatValue(key, valorAntes);
                depoisFormatado = formatValue(key, valorDepois);
                campoLabel = 'Comarca';
                camposProcessados.add('cidade_comarca');
            }
            
            mudancas.push({
                campo: campoLabel,
                antes: antesFormatado,
                depois: depoisFormatado
            });
        }
    }
    
    return mudancas;
};

export const mapearPartesEditadas = (logs: any[]): ParteAlteracao[] => {
    const partes: ParteAlteracao[] = [];
    
    const logsPartesProcesso = logs.filter((log: any) => log.entidade_nome === 'partes_processos');
    const logsPartesRepresentadas = logs.filter((log: any) => log.entidade_nome === 'partes_representadas');
    
    for (const log of logsPartesProcesso) {
        const dadosAntes = parseJsonSafely(log.dados_antes);
        const dadosDepois = parseJsonSafely(log.dados_depois);
        
        const isDadosDepoisVazio = isDadosVazio(dadosDepois);
        const isDadosAntesVazio = isDadosVazio(dadosAntes);
        
        if (!isDadosDepoisVazio && isDadosAntesVazio) {
            partes.push({
                tipo: 'adicionada',
                parte: {
                    tipo: dadosDepois.tipo,
                    nome: dadosDepois.nome,
                    representada: dadosDepois.id_parte_representada != null
                }
            });
        } else if (!isDadosAntesVazio && isDadosDepoisVazio) {
            partes.push({
                tipo: 'removida',
                parte: {
                    tipo: dadosAntes.tipo,
                    nome: dadosAntes.nome,
                    representada: dadosAntes.id_parte_representada != null
                }
            });
        }
    }
    
    for (const log of logsPartesRepresentadas) {
        const dadosAntes = parseJsonSafely(log.dados_antes);
        const dadosDepois = parseJsonSafely(log.dados_depois);
        
        const isDadosDepoisVazio = isDadosVazio(dadosDepois);
        const isDadosAntesVazio = isDadosVazio(dadosAntes);
        
        if (!isDadosDepoisVazio && isDadosAntesVazio) {
            partes.push({
                tipo: 'adicionada',
                parte: {
                    tipo: 'PARTE REPRESENTADA',
                    nome: dadosDepois.nome,
                    representada: true
                }
            });
        } else if (!isDadosAntesVazio && isDadosDepoisVazio) {
            partes.push({
                tipo: 'removida',
                parte: {
                    tipo: 'PARTE REPRESENTADA',
                    nome: dadosAntes.nome,
                    representada: true
                }
            });
        }
    }
    
    return partes;
};

export const mapearAnexosEditados = (logs: any[]): AnexoAlteracao[] => {
    const anexos: AnexoAlteracao[] = [];
    
    const logsAnexos = logs.filter((log: any) => log.entidade_nome === 'proposta_arquivo');
    
    for (const log of logsAnexos) {
        const dadosAntes = parseJsonSafely(log.dados_antes);
        const dadosDepois = parseJsonSafely(log.dados_depois);
        const dadosExtra = parseJsonSafely(log.dados_extra);
        
        const isDadosDepoisVazio = isDadosVazio(dadosDepois);
        const isDadosAntesVazio = isDadosVazio(dadosAntes);
        
        if (!isDadosDepoisVazio && isDadosAntesVazio) {
            const caminho = dadosDepois.caminho || dadosDepois.arquivo_final;
            const filename = extractFilename(caminho);
            const tamanhoArquivo = dadosExtra?.tamanho || dadosDepois.tamanho;
            const size = formatFileSize(tamanhoArquivo);
            
            if (caminho && filename && filename !== '—') {
                anexos.push({
                    tipo: 'adicionado',
                    anexo: {
                        id: log.entidade_id,
                        filename,
                        size: size !== '—' ? size : '—'
                    }
                });
            }
        } else if (!isDadosAntesVazio && isDadosDepoisVazio) {
            const caminho = dadosAntes.caminho || dadosAntes.arquivo_final;
            const filename = extractFilename(caminho);
            const tamanhoArquivo = dadosExtra?.tamanho || dadosAntes.tamanho;
            const size = formatFileSize(tamanhoArquivo);
            
            if (caminho && filename && filename !== '—') {
                anexos.push({
                    tipo: 'removido',
                    anexo: {
                        id: log.entidade_id,
                        filename,
                        size: size !== '—' ? size : '—'
                    }
                });
            }
        }
    }
    
    return anexos;
};

